import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AppDetails.css';
import CheckCircle from '../assets/img/CheckCircle.svg';
import CrossCircle from '../assets/img/CrossCircle.svg';
import {
  Search,
  Filter,
  Eye,
  Pencil,
  Trash2,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  MessageSquareText,
  X,
  Check
} from 'lucide-react';
import { useAzureData } from '../context/AzureContext';
import { patchOnboardingStatus } from '../service/apiServices';
import TooltipWrapper from './TooltipWrapper';
import { CloudProviderFilter } from './inputField';
import useCloudProvider from '../hooks/useCloudProvider';

export type AppRecord = {
  onboarding_id: number;
  application_name?: string;
  cloud: string;
  model_name?: string[];
  aif_rag_access?: boolean;
  environment: string;
  tier?: string;
  onboardingstatus?: string | null;
  created?: string;
  requested_by?: string;
  rejected_comments?: string;
  primary_owner?: string;
  secondary_owner?: string;
  wl3_approver?: string;
  icc?: string;
  cc?: string;
  ai_inventory_id?: string;
  application_description?: string;
  adgroupforappaccess?: string[];
  spn_appid_env_map?: {
    environment: string;
    service_principal_name: string;
    application_id: string | null;
  }[];
  status?: string | null;
  onboardingautomationstatus?: string | null;
  error_message?: string | null;
};

type AppDetailsProps = {
  appList: AppRecord[];
  showRequestColumns?: boolean;
  refetchApps?: () => Promise<void>;
};

type AppStatus = 'Approved' | 'Rejected';

const AppDetails: React.FC<AppDetailsProps> = ({ appList, showRequestColumns, refetchApps }) => {
  const { graphData, apiToken } = useAzureData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    'all apps' | 'approved' | 'approved-processing' | 'pending' | 'rejected'
  >('all apps');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof AppRecord | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });

  const handleSort = (key: keyof AppRecord) => {
    if (sortConfig.key === key) {
      setSortConfig({ key, direction: sortConfig.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSortConfig({ key, direction: 'asc' });
    }
  };

  //
  const { cloudProvidersList, selectedCloud, selectCloud } = useCloudProvider();

  const { tempAdmin, tempOwner } = useAzureData();
  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showComment, setShowComment] = useState<null | string>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [viewApp, setViewApp] = useState<AppRecord | null>(null);
  const [actionModal, setActionModal] = useState<null | {
    type: 'accept' | 'reject';
    app: AppRecord;
    comment?: string;
  }>(null);

  // filter state
  const [filters, setFilters] = useState({
    createdOnStart: '',
    createdOnEnd: '',
    tier: '',
    rag: '',
    environment: '',
    requestedBy: ''
  });
  const [tempFilters, setTempFilters] = useState(filters);

  // ✅ Collect dropdown values (normalized)
  const uniqueTiers = useMemo(() => {
    const set = new Set<string>();
    appList.forEach((app) => {
      if (app.tier) {
        const normalized = app.tier.charAt(0).toUpperCase() + app.tier.slice(1).toLowerCase();
        set.add(normalized);
      }
    });
    return Array.from(set);
  }, [appList]);

  const uniqueEnvs = useMemo(() => {
    const set = new Map<string, string>(); // lowercase -> formatted

    appList.forEach((app) => {
      const envs: string[] = Array.isArray(app.environment)
        ? app.environment
        : typeof app.environment === 'string'
          ? app.environment.split(',') // split string into array
          : []; // fallback if undefined/null

      envs.forEach((env) => {
        const trimmed = env.trim();
        if (!trimmed) return; // skip empty strings
        const lower = trimmed.toLowerCase();
        if (!set.has(lower)) {
          const formatted = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
          set.set(lower, formatted);
        }
      });
    });

    return Array.from(set.values());
  }, [appList]);

  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);

    const pad = (n: number) => n.toString().padStart(2, '0');

    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // ✅ Apply search + filters (GLOBAL, no activeTab here)
  const searchFilteredList = useMemo(() => {
    let list = [...appList];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = list.filter(
        (app) =>
          app.application_name?.toLowerCase().includes(query) ||
          app.model_name?.some((m) => m.toLowerCase().includes(query))
      );
    }

    // Apply other filters
    if (filters.tier) {
      list = list.filter(
        (app) =>
          app.tier &&
          app.tier.charAt(0).toUpperCase() + app.tier.slice(1).toLowerCase() === filters.tier
      );
    }

    if (filters.environment) {
      const normalizedFilter = filters.environment.toLowerCase().replace(/[-\s]/g, '');
      list = list.filter((app) => {
        const envs = Array.isArray(app.environment) ? app.environment : [app.environment];
        return envs.some((env) => env?.toLowerCase().replace(/[-\s]/g, '') === normalizedFilter);
      });
    }

    if (selectedCloud) {
      // console.log();
      list = list?.filter((item) => item?.cloud_platform_id === selectedCloud);
    }

    if (filters.rag) {
      list = list.filter((app) =>
        filters.rag === 'Yes' ? app.aif_rag_access : !app.aif_rag_access
      );
    }

    if (filters.requestedBy) {
      list = list.filter((app) =>
        app.requested_by?.toLowerCase().includes(filters.requestedBy.toLowerCase())
      );
    }

    if (filters.createdOnStart || filters.createdOnEnd) {
      const start = filters.createdOnStart ? new Date(filters.createdOnStart + 'T00:00:00') : null;
      const end = filters.createdOnEnd ? new Date(filters.createdOnEnd + 'T23:59:59') : null;

      list = list.filter((app) => {
        if (!app.created) return false;
        const created = new Date(app.created);

        if (start && !end) return created >= start;
        if (!start && end) return created <= end;
        if (start && end) return created >= start && created <= end;

        return true;
      });
    }

    return list;
  }, [appList, searchQuery, filters, selectedCloud]);

  // console.log(searchFilteredList, 'vvvsearchFilteredList');

  const filteredList = useMemo(() => {
    let list = searchFilteredList;
    if (activeTab !== 'all apps') {
      list = list.filter((app) => app.status?.toLowerCase() === activeTab.toLowerCase());
    }
    return list;
  }, [searchFilteredList, activeTab]);

  const sortedList = useMemo(() => {
    if (!sortConfig.key) return filteredList;

    const key = sortConfig.key;

    const sorted = [...filteredList].sort((a, b) => {
      const valA = a[key];
      const valB = b[key];

      // handle dates
      if (key === 'created') {
        const dateA = typeof valA === 'string' ? new Date(valA) : new Date(0);
        const dateB = typeof valB === 'string' ? new Date(valB) : new Date(0);
        return sortConfig.direction === 'asc'
          ? dateB.getTime() - dateA.getTime() // 👈 flipped
          : dateA.getTime() - dateB.getTime();
      }

      // handle numbers
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortConfig.direction === 'asc' ? valB - valA : valA - valB; // 👈 flipped
      }

      // handle booleans
      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return sortConfig.direction === 'asc'
          ? Number(valB) - Number(valA) // 👈 flipped
          : Number(valA) - Number(valB);
      }

      // fallback: treat as strings
      const strA = (Array.isArray(valA) ? valA.join(', ') : (valA?.toString() ?? '')).toLowerCase();
      const strB = (Array.isArray(valB) ? valB.join(', ') : (valB?.toString() ?? '')).toLowerCase();

      if (strA < strB) return sortConfig.direction === 'asc' ? 1 : -1; // 👈 flipped
      if (strA > strB) return sortConfig.direction === 'asc' ? -1 : 1; // 👈 flipped
      return 0;
    });

    return sorted;
  }, [filteredList, sortConfig]);

  const tabCounts = useMemo(() => {
    const total = searchFilteredList.length;

    const approved = searchFilteredList.filter(
      (app) => app.status?.toLowerCase() === 'approved'
    ).length;

    const approvedProcessing = searchFilteredList.filter(
      (app) => app.status?.toLowerCase() === 'approved-processing'
    ).length;

    const pending = searchFilteredList.filter(
      (app) => app.status?.toLowerCase() === 'pending'
    ).length;

    const rejected = searchFilteredList.filter(
      (app) => app.status?.toLowerCase() === 'rejected'
    ).length;

    return {
      'all apps': total,
      approved,
      'approved-processing': approvedProcessing,
      pending,
      rejected
    };
  }, [searchFilteredList]);

  // ✅ Pagination
  const totalPages = Math.max(1, Math.ceil(filteredList.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRecords = sortedList.slice(startIndex, startIndex + rowsPerPage);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setShowFilter(false);
    setCurrentPage(1);
  };

  const handleResetBox = (key: keyof typeof tempFilters) => {
    setTempFilters((prev) => ({ ...prev, [key]: '' }));
  };

  const handleResetAll = () => {
    selectCloud(0);
    setTempFilters({
      createdOnStart: '',
      createdOnEnd: '',
      tier: '',
      rag: '',
      environment: '',
      requestedBy: ''
    });
  };

  const handleAppRequestAction = async (
    app: AppRecord,
    status: AppStatus,
    comment: string = ''
  ) => {
    try {
      const payload = { status, comment };

      // console.log('Sending to backend:', { onboardingId: app.onboarding_id, payload });

      const response = await patchOnboardingStatus(app.onboarding_id, payload, apiToken);
      // console.log('Response from backend:', response);

      if (refetchApps) {
        await refetchApps();
      }
    } catch (err) {
      console.error('Failed to update request', err);
    }
  };

  const isMyApp = (app: any) => {
    const lowerCaseText = (text: string) => {
      return text?.toLowerCase();
    };

    return [lowerCaseText(app?.primary_owner), lowerCaseText(app?.secondary_owner)]?.includes(
      lowerCaseText(graphData.mail || '')
    );
  };

  return (
    <div className="app-details-container">
      {/* Tabs + Toolbar */}
      <div className="app-toolbar">
        <div className="tabs" role="tablist" aria-label="My Apps tabs">
          {(['all apps', 'approved', 'pending', 'rejected', 'approved-processing'] as const).map(
            (tab) => (
              <button
                key={tab}
                role="tab"
                aria-selected={activeTab === tab}
                className={`tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}>
                <span className="tab-label">
                  {tab
                    .split(' ')
                    .map((word) => word[0].toUpperCase() + word.slice(1))
                    .join(' ')}
                </span>
                <span className="tab-count">{tabCounts[tab]}</span>
              </button>
            )
          )}
        </div>

        <div className="toolbar-right">
          <div className="search-box">
            <Search size={16} strokeWidth={1.5} color="#838FB4" />
            <input
              type="text"
              placeholder="Search"
              aria-label="Search apps"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Filter */}
          <div className="filter-container">
            <button
              className="filter-btn"
              type="button"
              onClick={() => setShowFilter((prev) => !prev)}>
              <Filter size={20} strokeWidth={2} />
              <span>Filter</span>
            </button>

            {showFilter && (
              <div className="filter-popup">
                {/* Header */}
                <div className="filter-header">
                  Filter
                  <button className="close-btn" onClick={() => setShowFilter(false)}>
                    <X size={20} strokeWidth={2.5} color="#838FB4" />
                  </button>
                </div>

                {/* Created On */}
                <div className="filter-section">
                  <label>{showRequestColumns ? 'Requested On' : 'Created On'}</label>
                  <button
                    className="reset-link"
                    onClick={() =>
                      setTempFilters((prev) => ({ ...prev, createdOnStart: '', createdOnEnd: '' }))
                    }>
                    Reset
                  </button>
                  <div className="date-range">
                    <input
                      type="date"
                      value={tempFilters.createdOnStart}
                      onChange={(e) =>
                        setTempFilters((prev) => ({ ...prev, createdOnStart: e.target.value }))
                      }
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={tempFilters.createdOnEnd}
                      onChange={(e) =>
                        setTempFilters((prev) => ({ ...prev, createdOnEnd: e.target.value }))
                      }
                    />
                  </div>
                </div>
                {/* Tier */}
                <div className="filter-section">
                  <label>Tier</label>
                  <button className="reset-link" onClick={() => handleResetBox('tier')}>
                    Reset
                  </button>
                  <select
                    value={tempFilters.tier}
                    onChange={(e) => setTempFilters((prev) => ({ ...prev, tier: e.target.value }))}>
                    <option value="">Select</option>
                    {uniqueTiers.map((tier) => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Cloud Provider */}
                <CloudProviderFilter
                  cloudProvidersList={cloudProvidersList}
                  selectedCloud={selectedCloud}
                  selectCloud={selectCloud}
                />

                {/* Onboarded to RAG */}
                <div className="filter-section">
                  <label>Onboarded to RAG</label>
                  <button className="reset-link" onClick={() => handleResetBox('rag')}>
                    Reset
                  </button>
                  <select
                    value={tempFilters.rag}
                    onChange={(e) => setTempFilters((prev) => ({ ...prev, rag: e.target.value }))}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                {/* Environment */}
                <div className="filter-section">
                  <label>Environment</label>
                  <button className="reset-link" onClick={() => handleResetBox('environment')}>
                    Reset
                  </button>
                  <select
                    value={tempFilters.environment}
                    onChange={(e) =>
                      setTempFilters((prev) => ({ ...prev, environment: e.target.value }))
                    }>
                    <option value="">Select</option>
                    {uniqueEnvs.map((env) => (
                      <option key={env} value={env}>
                        {env}
                      </option>
                    ))}
                  </select>
                </div>

                {showRequestColumns && (
                  <div className="filter-section">
                    <label>Requested By</label>
                    <button className="reset-link" onClick={() => handleResetBox('requestedBy')}>
                      Reset
                    </button>
                    <select
                      value={tempFilters.requestedBy}
                      onChange={(e) =>
                        setTempFilters((prev) => ({ ...prev, requestedBy: e.target.value }))
                      }>
                      <option value="">Select</option>
                      {[...new Set(appList.map((app) => app.requested_by).filter(Boolean))].map(
                        (name, i) => (
                          <option key={i} value={name!}>
                            {name}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                )}
                {/* Footer */}
                <div className="filter-footer">
                  <button className="reset-all" onClick={handleResetAll}>
                    Reset All
                  </button>
                  <button className="apply-btn" onClick={handleApplyFilters}>
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="apps-table-container">
        {/* Header */}
        <div className="apps-table-header">
          <div className={`apps-table-row ${showRequestColumns ? 'request-columns' : ''}`}>
            <div className="apps-col-header tier" onClick={() => handleSort('tier')}>
              Tier
              <ArrowDown
                strokeWidth={2}
                size={18}
                className={`sort-icon ${sortConfig.key === 'tier' ? sortConfig.direction : ''}`}
              />
            </div>
            <div className="apps-col-header appname" onClick={() => handleSort('application_name')}>
              Application Name
              <ArrowDown
                strokeWidth={2}
                size={18}
                className={`sort-icon ${
                  sortConfig.key === 'application_name' ? sortConfig.direction : ''
                }`}
              />
            </div>
            <div className="apps-col-header models" onClick={() => handleSort('model_name')}>
              Models
              <ArrowDown
                strokeWidth={2}
                size={18}
                className={`sort-icon ${
                  sortConfig.key === 'model_name' ? sortConfig.direction : ''
                }`}
              />
            </div>
            <div className="apps-col-header rag">Onboarded to RAG</div>
            <div className="apps-col-header env d-flex justify-content-evenly align-items-center">
              <span className="d-flex justify-content-start" style={{ width: '35%' }}>
                Env
              </span>
              <span className="d-flex justify-content-center" style={{ width: '65%' }}>
                Cloud Provider
              </span>
            </div>
            <div
              className="apps-col-header created"
              onClick={() => handleSort(showRequestColumns ? 'created' : 'created')}>
              {showRequestColumns ? 'Requested on' : 'Created on'}
              <ArrowDown
                strokeWidth={2}
                size={18}
                className={`sort-icon ${sortConfig.key === 'created' ? sortConfig.direction : ''}`}
              />
            </div>
            {showRequestColumns && <div className="apps-col-header requested-by">Requested By</div>}

            <div className="apps-col-header status">Status</div>
            <div className="apps-col-header actions">Actions</div>
          </div>
        </div>

        {/* Body */}
        <div className="apps-table-body">
          {currentRecords.length > 0 ? (
            currentRecords.map((app) => (
              <div
                key={app.onboarding_id}
                className={`apps-table-row body-row ${
                  showRequestColumns ? 'request-columns' : ''
                } ${
                  showRequestColumns &&
                  app.status?.toLowerCase() === 'approved-processing' &&
                  app.error_message
                    ? 'row-error-highlight'
                    : ''
                }`}>
                <div className="apps-col_tier tier">
                  {app.tier ? (
                    <span
                      className={`tier-badge ${app.tier
                        ?.toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/[()]/g, '')}`}>
                      {app.tier
                        ? app.tier.charAt(0).toUpperCase() + app.tier.slice(1).toLowerCase()
                        : '-'}
                    </span>
                  ) : (
                    '-'
                  )}
                </div>
                <div className="apps-col appname">{app.application_name ?? '-'}</div>

                <div className="apps-col models">
                  {app.model_name?.length ? (
                    <div className="models-cell">
                      {/* First 2 models */}
                      {app.model_name.slice(0, 2).map((m, i) => (
                        <div key={i} className="model-pill">
                          {m}
                        </div>
                      ))}

                      {/* 3rd model (with +N if needed) */}
                      {app.model_name[2] && (
                        <div className="model-pill">
                          {app.model_name[2]}
                          {app.model_name.length > 3 && (
                            <span className="more-models">+{app.model_name.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    '-'
                  )}
                </div>

                <div className="apps-col rag">{app.aif_rag_access ? 'Yes' : 'No'}</div>
                <div className="apps-col env d-flex justify-content-evenly px-2">
                  <span className="d-flex justify-content-start" style={{ width: '35%' }}>
                    {(Array.isArray(app.environment) ? app.environment : [app.environment])
                      .filter(Boolean)
                      .map((env) => env.charAt(0).toUpperCase() + env.slice(1).toLowerCase())
                      .join(', ') || '-'}
                  </span>
                  <span className="d-flex justify-content-center" style={{ width: '65%' }}>
                    {cloudProvidersList?.filter((itm) => itm?.id === app?.cloud_platform_id)?.[0]
                      ?.name || 'NA'}
                  </span>
                </div>
                <div className="apps-col created">{formatDateTime(app.created)}</div>

                {showRequestColumns && (
                  <div className="apps-col requested-by">{app.requested_by ?? 'No Data'}</div>
                )}

                <div className="apps-col status">
                  {showRequestColumns &&
                  app.status?.toLowerCase() === 'approved-processing' &&
                  app.error_message ? (
                    <span
                      className={`status-badge p-1 ${(app.status ?? 'Pending').toLowerCase()}`}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      style={{ textWrap: 'wrap', textAlign: 'center' }}
                      title={app.error_message}>
                      {app.status?.replace('-', ' ') ?? 'Pending'}
                    </span>
                  ) : (
                    <span
                      className={`status-badge  p-1 ${(app.status ?? 'Pending').toLowerCase()}`}
                      style={{ textWrap: 'wrap', textAlign: 'center' }}>
                      {app.status?.replace('-', ' ') ?? 'Pending'}
                    </span>
                  )}

                  {app.status?.toLowerCase() === 'rejected' && app.rejected_comments && (
                    <button
                      className="comment-icon-btn"
                      onClick={() => setShowComment(app.rejected_comments!)}
                      title="View Comments">
                      <MessageSquareText size={18} strokeWidth={1.8} />
                    </button>
                  )}
                </div>

                <div className="apps-col actions">
                  {/* View button always present */}
                  <button className="action-btn" onClick={() => setViewApp(app)}>
                    <Eye size={20} strokeWidth={1.7} />
                  </button>
                  {showRequestColumns ? (
                    <>
                      {/* Accept button */}
                      <button
                        className="action-btn"
                        onClick={() => setActionModal({ type: 'accept', app })}
                        disabled={['approved', 'rejected', 'approved-processing'].includes(
                          app.status?.toLowerCase() || ''
                        )}
                        title={
                          ['approved', 'rejected', 'approved-processing'].includes(
                            app.status?.toLowerCase() || ''
                          )
                            ? 'Cannot approve/reject this app'
                            : 'Approve'
                        }
                        style={{
                          opacity: ['approved', 'rejected', 'approved-processing'].includes(
                            app.status?.toLowerCase() || ''
                          )
                            ? 0.4
                            : 1,
                          pointerEvents: ['approved', 'rejected', 'approved-processing'].includes(
                            app.status?.toLowerCase() || ''
                          )
                            ? 'none'
                            : 'auto',
                          filter: ['approved-processing'].includes(app.status?.toLowerCase() || '')
                            ? 'blur(1px)'
                            : 'none' // 👈 subtle blur effect
                        }}>
                        <Check size={20} strokeWidth={2} />
                      </button>

                      {/* Reject button */}
                      <button
                        className="action-btn"
                        onClick={() => {
                          // Treat "Approved-Processing" as "Pending"
                          const normalizedStatus =
                            app.status?.toLowerCase() === 'approved-processing'
                              ? 'pending'
                              : app.status?.toLowerCase();
                          setActionModal({
                            type: 'reject',
                            app: { ...app, status: normalizedStatus }
                          });
                        }}
                        disabled={['approved', 'rejected'].includes(
                          app.status?.toLowerCase() || ''
                        )}
                        title={
                          ['approved', 'rejected'].includes(app.status?.toLowerCase() || '')
                            ? 'Cannot approve/reject this app'
                            : 'Reject'
                        }
                        style={{
                          opacity: ['approved', 'rejected'].includes(
                            app.status?.toLowerCase() || ''
                          )
                            ? 0.4
                            : 1,
                          pointerEvents: ['approved', 'rejected'].includes(
                            app.status?.toLowerCase() || ''
                          )
                            ? 'none'
                            : 'auto'
                        }}>
                        <X size={20} strokeWidth={2} />
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Edit button for normal apps */}

                      {(tempAdmin || isMyApp(app)) && (
                        <button
                          className="action-btn"
                          onClick={() =>
                            navigate('/onboarding', { state: { editAppId: app.onboarding_id } })
                          }
                          title={
                            app.status?.toLowerCase() === 'approved-processing'
                              ? 'Edit disabled while approval is processing'
                              : 'Edit'
                          }
                          disabled={app.status?.toLowerCase() === 'approved-processing'}
                          style={{
                            opacity: app.status?.toLowerCase() === 'approved-processing' ? 0.4 : 1,
                            pointerEvents:
                              app.status?.toLowerCase() === 'approved-processing' ? 'none' : 'auto',
                            filter:
                              app.status?.toLowerCase() === 'approved-processing'
                                ? 'blur(1px)'
                                : 'none'
                          }}>
                          <Pencil size={20} strokeWidth={1.7} />
                        </button>
                      )}

                      {/* Delete button for normal apps */}
                      {/* <button className="action-btn">
                          <Trash2 size={20} strokeWidth={1.7} />
                        </button> */}
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">No apps to display.</div>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="apps-table-footer">
          <div className="footer-left">
            Total: <span className="footer-count">{filteredList.length}</span>
          </div>
          <div className="footer-center">
            <button className="page-btn" onClick={handlePrev} disabled={currentPage === 1}>
              <ChevronLeft size={18} strokeWidth={2} />
            </button>
            <div className="page-info">
              <span className="page-number">{currentPage}</span>
              <span className="of-text">of</span>
              <span className="page-number total">{totalPages}</span>
            </div>
            <button className="page-btn" onClick={handleNext} disabled={currentPage === totalPages}>
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </div>
          <div className="footer-right">
            Show per page:{' '}
            <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              {Array.from(
                { length: Math.ceil(filteredList.length / 10) },
                (_, i) => (i + 1) * 10
              ).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Comments Modal */}
      {showComment && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Comments</h3>
              <button className="modal-close" onClick={() => setShowComment(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>{showComment}</p>
            </div>
          </div>
        </div>
      )}
      {viewApp && (
        <div className="side-modal-overlay">
          <div className="side-modal">
            <div className="side-modal-header">
              <div>
                <h2 className="app-title">{viewApp.application_name}</h2>
                <p className="app-subtitle">Application details</p>
              </div>
              <button className="modal-close" onClick={() => setViewApp(null)}>
                <X size={20} strokeWidth={2.5} color="#838FB4" />
              </button>
            </div>

            <div className="side-modal-body">
              <div className="info-row">
                <span className="label">Environment</span>
                <span>{viewApp.environment ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Tier</span>
                <span className={`tier-badge ${viewApp.tier?.toLowerCase()}`}>
                  {viewApp.tier
                    ? viewApp.tier.charAt(0).toUpperCase() + viewApp.tier.slice(1).toLowerCase()
                    : '-'}
                </span>
              </div>
              {/* ✅ Show these two rows only if showRequestColumns is true */}
              {showRequestColumns && (
                <div className="info-row">
                  <span className="label">Automation Status</span>
                  <div className="status-column">
                    {viewApp.onboardingautomationstatus ? (
                      <>
                        <span
                          className={`status-badge ${
                            viewApp.onboardingautomationstatus === 'Failed'
                              ? 'rejected'
                              : viewApp.onboardingautomationstatus === 'Completed'
                                ? 'approved'
                                : ''
                          }`}>
                          {viewApp.onboardingautomationstatus}
                        </span>

                        {/* Show error message directly below the status if Failed */}
                        {viewApp.onboardingautomationstatus === 'Failed' &&
                          viewApp.error_message && (
                            <div className="error-message" style={{ marginTop: '4px' }}>
                              {viewApp.error_message}
                            </div>
                          )}
                      </>
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                </div>
              )}

              <div className="description-box">
                <h4>Application Description</h4>
                <p>
                  {viewApp.application_description ??
                    'No description available for this application.'}
                </p>
              </div>

              <div className="section-title">Additional Information</div>
              <div className="info-row">
                <span className="label">Primary Owner</span>
                <span>{viewApp.primary_owner ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Secondary Owner</span>
                <span>{viewApp.secondary_owner ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">WL3+ Approver</span>
                <span>{viewApp.wl3_approver ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">ICC</span>
                <span>{viewApp.icc ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">CC</span>
                <span>{viewApp.cc ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">AI Inventory ID</span>
                <span>{viewApp.ai_inventory_id ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Model</span>
                <span>{viewApp.model_name?.join(', ') ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">RAG Builder Subscription</span>
                <span>{viewApp.aif_rag_access ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {actionModal && (
        <div className="action-modal-backdrop">
          <div className="action-modal">
            <img
              src={actionModal.type === 'accept' ? CheckCircle : CrossCircle}
              alt={actionModal.type}
              className="action-modal-icon"
            />

            <h3 className={`action-modal-title ${actionModal.type}`}>
              {actionModal.type === 'accept'
                ? `Are you sure you want to accept "${actionModal.app.application_name}" ?`
                : `Are you sure you want to reject "${actionModal.app.application_name}" ?`}
            </h3>

            {/* Only show comment textarea for reject */}
            {actionModal.type === 'reject' && (
              <textarea
                placeholder="Comment (optional)"
                className="action-modal-comment"
                value={actionModal.comment || ''}
                onChange={(e) => setActionModal({ ...actionModal, comment: e.target.value })}
              />
            )}

            <div className="action-modal-actions">
              <button className="btn-cancel" onClick={() => setActionModal(null)}>
                Cancel
              </button>
              <button
                className={`btn-confirm ${actionModal.type}`}
                onClick={() => {
                  handleAppRequestAction(
                    actionModal.app,
                    actionModal.type === 'accept' ? 'Approved' : 'Rejected',
                    actionModal.comment || '' // pass the comment
                  );
                  setActionModal(null);
                }}>
                {actionModal.type === 'accept' ? 'Accept' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppDetails;
